import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  apiResponseEnvelopeSchema,
  type TokenResponse
} from '../../../shared/api/contracts/styleTransferSchemas';
import { STYLE_CATALOG_QUERY_KEY } from '../api/useStyleCatalog';
import { logSyncFailure } from '../telemetry/syncDiagnostics';
import { adaptImageStyleResponse, type ImageStyleResult } from '../../../shared/api/intelliSightAdapters';
import { fetchWithAuthRetry } from '../../auth/api/authenticatedRequest';

export interface StyleTransferPayload {
  file: File;
  styleId: string;
  token: TokenResponse;
  signal?: AbortSignal;
  backgroundColorCode?: string | null;
}

export interface StyleTransferSuccess {
  image: ImageStyleResult;
  remainingRequests: number | null;
}

async function submitStyleTransfer({
  file,
  styleId,
  token,
  signal,
  backgroundColorCode
}: StyleTransferPayload): Promise<StyleTransferSuccess> {
  const baseUrl = import.meta.env.VITE_API_BASE_URL;
  if (!baseUrl) {
    throw new Error('VITE_API_BASE_URL is not defined');
  }

  const formData = new FormData();
  formData.append('styleCode', styleId);
  formData.append('image', file);
  if (backgroundColorCode) {
    formData.append('backgroundColorCode', backgroundColorCode);
  }

  const response = await fetchWithAuthRetry({
    authToken: token,
    retryLabel: 'style-transfer-upload',
    execute: async (authHeaders) =>
      fetch(new URL('/api/v1/image-styles/transform', baseUrl), {
        method: 'POST',
        headers: {
          ...authHeaders
        },
        body: formData,
        signal
      })
  });

  const contentType = response.headers.get('Content-Type') ?? '';
  if (!response.ok) {
    if (contentType.includes('application/json')) {
      const payload = await response.json();
      const envelope = apiResponseEnvelopeSchema.safeParse(payload);
      const message =
        (envelope.success && typeof envelope.data.message === 'string' && envelope.data.message) ||
        (typeof (payload as { message?: string }).message === 'string'
          ? (payload as { message?: string }).message
          : response.statusText);
      throw new Error(message || `Upload failed with status ${response.status}`);
    }

    throw new Error(`Upload failed with status ${response.status}`);
  }

  if (!contentType.includes('application/json')) {
    throw new Error('Unexpected response format from transform endpoint');
  }

  const payload = (await response.json()) as unknown;
  const envelope = apiResponseEnvelopeSchema.safeParse(payload);
  const data = envelope.success ? envelope.data.data : payload;
  const remainingHeader = response.headers.get('X-Remaining-Requests');
  const remainingRequests = typeof remainingHeader === 'string'
    ? Number.parseInt(remainingHeader, 10)
    : Number.NaN;

  return {
    image: adaptImageStyleResponse(data),
    remainingRequests: Number.isFinite(remainingRequests) ? remainingRequests : null
  };
}

export function useStyleTransferMutation() {
  const queryClient = useQueryClient();

  return useMutation<StyleTransferSuccess, Error, StyleTransferPayload>({
    mutationKey: ['style-transfer', 'upload'],
    mutationFn: async (payload) => {
      try {
        const result = await submitStyleTransfer(payload);
        return result;
      } catch (error) {
        logSyncFailure(
          {
            resource: 'history',
            metadata: {
              styleId: payload.styleId,
              backgroundColorCode: payload.backgroundColorCode ?? undefined
            }
          },
          error
        );
        throw error;
      }
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: STYLE_CATALOG_QUERY_KEY });
    }
  });
}
